import { io } from "socket.io-client";

// Unified Regames backend (Socket.IO namespace /guessparty).
// Override locally with REACT_APP_API_URL=http://localhost:4000 for dev.
const API_URL =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === "development"
    ? "http://localhost:4000"
    : "https://regames-backend.onrender.com");

const SOCKET_URL = `${API_URL}/guessparty`;

class SocketService {
  constructor() {
    this.socket = null;
    this.playerId = null;
    this.reconnectAttempted = false;
    this.allowAutoReconnect = false; // Désactivé par défaut
  }

  connect() {
    if (!this.socket) {
      this.socket = io(SOCKET_URL, {
        autoConnect: true,
      });

      this.socket.on("connect", () => {
        console.log("Connecté au serveur Socket.IO");

        // Tenter une reconnexion si on a un playerId sauvegardé ET que c'est autorisé
        if (
          !this.reconnectAttempted &&
          this.playerId &&
          this.allowAutoReconnect
        ) {
          const savedRoomId = localStorage.getItem("roomId");
          if (savedRoomId) {
            console.log("Tentative de reconnexion automatique...");
            this.socket.emit("reconnect", {
              playerId: this.playerId,
              roomId: savedRoomId,
            });
            this.reconnectAttempted = true;
          }
        }
      });

      this.socket.on("disconnect", () => {
        console.log("Déconnecté du serveur");
        this.reconnectAttempted = false;
      });

      this.socket.on("error", (data) => {
        console.error("Erreur Socket.IO:", data.message);
        alert(data.message);
      });

      // Écouter l'attribution du playerId
      this.socket.on("playerIdAssigned", ({ playerId }) => {
        console.log("PlayerId reçu:", playerId);
        this.playerId = playerId;
        localStorage.setItem("playerId", playerId);
      });

      // reconnectSuccess est géré via _on dans GameContext
      // La logique de sauvegarde est intégrée dans le wrapper ci-dessous

      // Écouter l'échec de reconnexion
      this.socket.on("reconnectFailed", ({ message }) => {
        console.warn("Échec de reconnexion:", message);
        // Nettoyer les données sauvegardées
        localStorage.removeItem("playerId");
        localStorage.removeItem("roomId");
        this.playerId = null;
      });
    }
    return this.socket;
  }

  loadSavedPlayerId() {
    const savedPlayerId = localStorage.getItem("playerId");
    if (savedPlayerId) {
      this.playerId = savedPlayerId;
      console.log("PlayerId chargé depuis localStorage:", savedPlayerId);
    }
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  getSocket() {
    if (!this.socket) {
      return this.connect();
    }
    return this.socket;
  }

  // Événements à envoyer
  joinRoom(roomId) {
    localStorage.setItem("roomId", roomId);
    const avatar = localStorage.getItem("selectedAvatar") || null;
    this.getSocket().emit("joinRoom", { roomId, playerId: this.playerId, avatar });
  }

  setPseudo(pseudo) {
    this.getSocket().emit("setPseudo", { pseudo });
  }

  setAvatar(avatar) {
    this.getSocket().emit("setAvatar", { avatar });
  }

  playerReady() {
    this.getSocket().emit("playerReady");
  }

  submitCharacter(characterName, hint) {
    this.getSocket().emit("submitCharacter", { characterName, hint: hint || null });
  }

  passTurn() {
    this.getSocket().emit("passTurn");
  }

  guessCharacter(guess) {
    this.getSocket().emit("guessCharacter", { guess });
  }

  voteToCancel() {
    this.getSocket().emit("voteToCancel");
  }

  kickPlayer(targetPlayerId) {
    this.getSocket().emit("kickPlayer", { targetPlayerId });
  }

  leaveRoom() {
    this.getSocket().emit("leaveRoom");
  }

  setTheme(theme) {
    this.getSocket().emit("setTheme", { theme });
  }

  setHintsEnabled(enabled) {
    this.getSocket().emit("setHintsEnabled", { enabled });
  }

  requestHint() {
    this.getSocket().emit("requestHint");
  }

  joinAsSpectator(roomId) {
    localStorage.setItem("roomId", roomId);
    this.getSocket().emit("joinAsSpectator", { roomId });
  }

  setRandomMode(enabled) {
    this.getSocket().emit("setRandomMode", { enabled });
  }

  approveHintRequest(targetPlayerId) {
    this.getSocket().emit("approveHintRequest", { targetPlayerId });
  }

  rejectHintRequest(targetPlayerId) {
    this.getSocket().emit("rejectHintRequest", { targetPlayerId });
  }

  launchNewRound() {
    this.getSocket().emit("launchNewRound");
  }

  // Écouter les événements (off avant on pour éviter les doublons en StrictMode)
  _on(event, callback) {
    const s = this.getSocket();
    s.off(event);
    s.on(event, callback);
  }

  onRoomUpdate(callback) { this._on("roomUpdate", callback); }
  onAvatarUpdated(callback) { this._on("avatarUpdated", callback); }
  onGameStarted(callback) { this._on("gameStarted", callback); }
  onCharactersAssigned(callback) { this._on("charactersAssigned", callback); }
  onTurnChanged(callback) { this._on("turnChanged", callback); }
  onPlayerWon(callback) { this._on("playerWon", callback); }
  onGameFinished(callback) { this._on("gameFinished", callback); }
  onWrongGuess(callback) { this._on("wrongGuess", callback); }
  onPlayerDisconnected(callback) { this._on("playerDisconnected", callback); }
  onPlayerReconnected(callback) { this._on("playerReconnected", callback); }
  onReconnectSuccess(callback) {
    const self = this;
    this._on("reconnectSuccess", (data) => {
      self.playerId = data.playerId;
      localStorage.setItem("playerId", data.playerId);
      localStorage.setItem("roomId", data.roomId);
      callback(data);
    });
  }
  onPlayerRemoved(callback) { this._on("playerRemoved", callback); }
  onCancelVoteUpdate(callback) { this._on("cancelVoteUpdate", callback); }
  onRoomCancelled(callback) { this._on("roomCancelled", callback); }
  onPlayerKicked(callback) { this._on("playerKicked", callback); }
  onForceLeaveRoom(callback) { this._on("forceLeaveRoom", callback); }
  onHintRequested(callback) { this._on("hintRequested", callback); }
  onHintRevealed(callback) { this._on("hintRevealed", callback); }
  onHintApproved(callback) { this._on("hintApproved", callback); }
  onHintRejected(callback) { this._on("hintRejected", callback); }
  onHintRejectedByMe(callback) { this._on("hintRejectedByMe", callback); }
  onSpectatorJoined(callback) { this._on("spectatorJoined", callback); }

  getPlayerId() {
    return this.playerId;
  }

  // Gestion de session
  hasActiveSession() {
    const savedPlayerId = localStorage.getItem("playerId");
    const savedRoomId = localStorage.getItem("roomId");
    return !!(savedPlayerId && savedRoomId);
  }

  getSavedRoomId() {
    return localStorage.getItem("roomId");
  }

  clearSession() {
    console.log("Nettoyage de la session...");
    localStorage.removeItem("playerId");
    localStorage.removeItem("roomId");
    this.playerId = null;
    this.reconnectAttempted = false;
    this.allowAutoReconnect = false;
  }

  enableAutoReconnect() {
    console.log("Auto-reconnexion activée");
    this.allowAutoReconnect = true;
  }

  disableAutoReconnect() {
    console.log("Auto-reconnexion désactivée");
    this.allowAutoReconnect = false;
  }

  // Forcer une reconnexion manuelle
  manualReconnect() {
    if (this.reconnectAttempted) return true;

    const savedRoomId = localStorage.getItem("roomId");
    const savedPlayerId = localStorage.getItem("playerId");

    if (!savedRoomId || !savedPlayerId) {
      console.error("Impossible de se reconnecter : données manquantes");
      return false;
    }

    this.playerId = savedPlayerId;
    this.allowAutoReconnect = true;
    this.reconnectAttempted = true;

    console.log(`Reconnexion manuelle à la room ${savedRoomId}...`);
    this.getSocket().emit("reconnect", {
      playerId: savedPlayerId,
      roomId: savedRoomId,
    });

    return true;
  }

  // Retirer les listeners
  off(eventName, callback) {
    this.getSocket().off(eventName, callback);
  }
}

const socketService = new SocketService();
export default socketService;
